import { definedMapWithDefault, definedMap } from "collection-utils";

import {
    TableGlideType,
    TableColumn,
    Description,
    InputOutputTables,
    MutatingScreenKind,
    SourceColumn,
    SourceColumnKind,
    getTableName,
    getTableColumn,
    getInputOrOutputTable,
    filterEditableColumns,
    makeSourceColumn,
    allowProtectedColumns,
} from "./description";
import {
    ColumnPropertyDescriptorCase,
    ComponentConfiguratorContext,
    isNativeTableInAppDescriptionContext,
} from "./components";
import { hasTableFavorites } from "./favorites";
import { isFavoritedColumnName } from "../google-sheets";
import { getColumnForSourceColumn } from "./source-column";

export function getAllowedColumnsFromTable<T extends Description>(
    descr: ColumnPropertyDescriptorCase<T>,
    table: TableGlideType,
    desc: T,
    ccc: ComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind | undefined
): { allowed: readonly TableColumn[]; avoid: TableColumn | undefined } {
    const hasFavorites = hasTableFavorites(getTableName(table), ccc);
    const allowProtected = allowProtectedColumns(descr.isEditedInApp, mutatingScreenKind);
    const columns = descr
        .getAllowedColumns(table, desc, ccc)
        .filter(
            c =>
                c.hidden !== true &&
                (allowProtected || c.isProtected !== true) &&
                (hasFavorites || c.name !== isFavoritedColumnName)
        );
    const avoid = definedMap(table.rowIDColumn, n => getTableColumn(table, n));
    return { allowed: columns, avoid };
}

// FIXME: Upgrade for user profile columns
export function getAllowedColumnsForProperty<T extends Description>(
    descr: ColumnPropertyDescriptorCase<T>,
    tables: InputOutputTables,
    rootDesc: T,
    desc: T,
    ccc: ComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind | undefined
): { table: TableGlideType | undefined; allowed: readonly TableColumn[]; avoid: TableColumn | undefined } {
    const propertyTable = definedMapWithDefault(
        descr.getIndirectTable,
        getInputOrOutputTable(tables, descr.isEditedInApp === true),
        f => f(tables, rootDesc, ccc)
    );
    if (propertyTable === undefined) {
        return { table: undefined, allowed: [], avoid: undefined };
    }
    const allowed = getAllowedColumnsFromTable(descr, propertyTable, desc, ccc, mutatingScreenKind);
    if (descr.isEditedInApp !== true) {
        return { table: propertyTable, ...allowed };
    }
    const isNative = isNativeTableInAppDescriptionContext(ccc, getTableName(tables.output));
    return {
        table: propertyTable,
        allowed: filterEditableColumns(tables.output, isNative, allowed.allowed, mutatingScreenKind),
        avoid: allowed.avoid,
    };
}

export interface AllowedColumns {
    readonly contextTable: TableGlideType | undefined;
    readonly context: readonly TableColumn[];

    readonly userProfileTable: TableGlideType | undefined;
    readonly userProfile: readonly TableColumn[];

    readonly containingScreenTable?: TableGlideType;
    readonly containingScreen?: readonly TableColumn[];
}

export function getDefaultSourceColumn(columns: AllowedColumns): SourceColumn | undefined {
    if (columns.context.length > 0) {
        return makeSourceColumn(columns.context[0].name, SourceColumnKind.DefaultContext);
    } else if (columns.userProfile.length > 0) {
        return makeSourceColumn(columns.userProfile[0].name, SourceColumnKind.UserProfile);
    }
    return undefined;
}

export interface AllowedColumnsForProperty extends AllowedColumns {
    readonly contextAvoid: TableColumn | undefined;
    readonly userProfileAvoid: TableColumn | undefined;
}

function getAllowedColumns<T extends Description>(
    columnCase: ColumnPropertyDescriptorCase<T>,
    tables: InputOutputTables,
    rootDesc: T,
    desc: T,
    ccc: ComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind | undefined
): AllowedColumnsForProperty | undefined {
    const fromContext =
        mutatingScreenKind === MutatingScreenKind.AddScreen && columnCase.needsExistingValue === true
            ? { table: undefined, allowed: [], avoid: undefined }
            : getAllowedColumnsForProperty(columnCase, tables, rootDesc, desc, ccc, mutatingScreenKind);
    let userProfileAllowed: readonly TableColumn[] = [];
    let userProfileAvoid: TableColumn | undefined;
    let userProfileTable: TableGlideType | undefined;
    if (columnCase.allowUserProfileColumns === true) {
        const userProfileTableName = ccc.userProfileTableInfo?.tableName;
        if (userProfileTableName !== undefined) {
            userProfileTable = ccc.findTable(userProfileTableName);
            if (userProfileTable !== undefined) {
                const fromUserProfile = getAllowedColumnsFromTable(
                    columnCase,
                    userProfileTable,
                    desc,
                    ccc,
                    mutatingScreenKind
                );
                userProfileAllowed = fromUserProfile.allowed;
                userProfileAvoid = fromUserProfile.avoid;
            }
        }
    }

    if (fromContext.allowed.length === 0 && userProfileAllowed.length === 0) return undefined;

    return {
        contextTable: fromContext.table,
        context: fromContext.allowed,
        contextAvoid: fromContext.avoid,
        userProfile: userProfileAllowed,
        userProfileAvoid,
        userProfileTable,
    };
}

// FIXME: Get rid of this.  We should be operating on `SourceColumn` directly.
export interface AllowedAndSelectedColumns extends AllowedColumnsForProperty {
    readonly selectedSourceColumn: SourceColumn | undefined;
    readonly selectedColumn: TableColumn | undefined;
}

export function getAllowedAndSelectedColumns<T extends Description>(
    columnCase: ColumnPropertyDescriptorCase<T>,
    tables: InputOutputTables,
    selectedSourceColumn: SourceColumn | undefined,
    rootDesc: T,
    desc: T,
    ccc: ComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind | undefined
): AllowedAndSelectedColumns | undefined {
    const result = getAllowedColumns(columnCase, tables, rootDesc, desc, ccc, mutatingScreenKind);
    if (result === undefined) return undefined;

    let selectedColumn: TableColumn | undefined;
    if (selectedSourceColumn !== undefined) {
        selectedColumn = getColumnForSourceColumn(ccc, selectedSourceColumn, result.context, undefined);
    }

    return { ...result, selectedSourceColumn, selectedColumn };
}
